/**
 * `<BoardViewTabs>` — the tracker's view switcher.
 *
 * A tracker starts with just the Table view; more views (Kanban / Timeline /
 * Dashboard) are opt-in. The enabled views render as tabs (one-click switch,
 * drag to reorder) and a trailing menu lists the whole catalogue with a toggle
 * per view — so the area only ever holds tabs the user chose, never a wall of
 * buttons.
 *
 * Order matters: the FIRST tab is the view the tracker opens on (persisted in
 * `layout.views`, shared by the workspace). `table` is pinned — always present,
 * reorderable, but not removable.
 *
 * Tabs render in the user's saved order, NOT catalogue order, so a reorder
 * survives and the opens-by-default choice stays meaningful. The menu is the
 * one place that uses catalogue order, since it lists views that aren't on yet
 * and so have no position of their own.
 */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Switch,
} from '@monksflow/monks-ui';
import {
  CalendarRange,
  LayoutDashboard,
  Plus,
  SquareKanban,
  Table2,
  type LucideIcon,
} from 'lucide-react';

import type { TrackerViewType } from '@/entities/tracker';
import { useTranslation } from 'react-i18next';
import { useCanEditTracker } from '../../lib/tracker-access';
import type { ParseKeys } from 'i18next';

/** The pinned baseline view — always present, never removable. */
const PINNED_VIEW: TrackerViewType = 'table';

const VIEW_META: Record<
  TrackerViewType,
  { labelKey: ParseKeys<'trackers'>; descriptionKey: ParseKeys<'trackers'>; Icon: LucideIcon }
> = {
  table: {
    labelKey: 'trackersBoard.boardViewTabs.table',
    descriptionKey: 'trackersBoard.boardViewTabs.rowsGroupedBySection',
    Icon: Table2,
  },
  kanban: {
    labelKey: 'trackersBoard.boardViewTabs.kanban',
    descriptionKey: 'trackersBoard.boardViewTabs.cardsByStatusColumn',
    Icon: SquareKanban,
  },
  timeline: {
    labelKey: 'trackersBoard.boardViewTabs.timeline',
    descriptionKey: 'trackersBoard.boardViewTabs.itemsOnADateAxis',
    Icon: CalendarRange,
  },
  dashboard: {
    labelKey: 'trackersBoard.boardViewTabs.dashboard',
    descriptionKey: 'trackersBoard.boardViewTabs.chartsAndRollUpMetrics',
    Icon: LayoutDashboard,
  },
};

/** Menu order. Tabs follow the user's saved order instead. */
const VIEW_CATALOGUE: TrackerViewType[] = ['table', 'kanban', 'dashboard', 'timeline'];

interface BoardViewTabsProps {
  /** Enabled views, in order — the first is the default-on-open. */
  views: TrackerViewType[];
  /**
   * Views this board can offer at all, defaulting to the whole catalogue. A
   * board whose numbers come from the server passes a shorter list: Dashboard
   * and Timeline chart the rows in hand, which on a paged board is a picture of
   * the first page presented as a picture of everything.
   */
  catalogue?: readonly TrackerViewType[];
  activeView: TrackerViewType;
  onSwitch: (view: TrackerViewType) => void;
  onAddView: (view: TrackerViewType) => void;
  onRemoveView: (view: TrackerViewType) => void;
  onReorder: (next: TrackerViewType[]) => void;
}

export function BoardViewTabs({
  views,
  catalogue = VIEW_CATALOGUE,
  activeView,
  onSwitch,
  onAddView,
  onRemoveView,
  onReorder,
}: BoardViewTabsProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();

  const sensors = useSensors(
    // Distance constraint so a plain click still switches; only a real drag
    // (>4px) starts a reorder.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = views.indexOf(active.id as TrackerViewType);
    const to = views.indexOf(over.id as TrackerViewType);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(views, from, to));
  };

  return (
    <div className="flex items-center gap-1">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={views} strategy={horizontalListSortingStrategy}>
          <div className="flex items-center gap-0.5 rounded-lg bg-accent p-1">
            {views.map((view, index) => (
              <ViewTab
                key={view}
                view={view}
                active={view === activeView}
                isDefault={index === 0}
                onClick={() => onSwitch(view)}
                reorderable={canEdit}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {canEdit ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('trackersBoard.boardViewTabs.manageViews')}
              className="data-[state=open]:bg-accent"
            >
              <Plus aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <ViewToggleList
              views={views}
              catalogue={catalogue}
              onAddView={onAddView}
              onRemoveView={onRemoveView}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

interface ViewMenuProps {
  views: TrackerViewType[];
  catalogue: readonly TrackerViewType[];
  onAddView: (view: TrackerViewType) => void;
  onRemoveView: (view: TrackerViewType) => void;
}

/** The whole catalogue, on or off, each row a toggle. */
function ViewToggleList({ views, catalogue, onAddView, onRemoveView }: ViewMenuProps) {
  const { t } = useTranslation('trackers');

  return (
    <>
      <DropdownMenuLabel>{t('trackersBoard.boardViewTabs.views')}</DropdownMenuLabel>
      {VIEW_CATALOGUE.filter((view) => catalogue.includes(view)).map((view) => {
        const { labelKey, descriptionKey, Icon } = VIEW_META[view];
        const locked = view === PINNED_VIEW;
        const on = views.includes(view);
        return (
          <DropdownMenuCheckboxItem
            key={view}
            checked={on}
            onCheckedChange={(checked) => {
              if (locked) return;
              if (checked) onAddView(view);
              else onRemoveView(view);
            }}
            // Turning several views on is one visit, not one per view.
            onSelect={(e) => e.preventDefault()}
            // `aria-disabled` rather than `disabled`: Radix drops a disabled
            // item out of the menu's arrow navigation, which would hide the
            // pinned view from a keyboard or screen-reader user instead of
            // telling them it is on and cannot be turned off.
            aria-disabled={locked || undefined}
            // The switch on the right IS this row's state, so the built-in
            // left check is suppressed along with the space it reserves.
            className={`items-start gap-2 pl-2 [&>span:first-child]:hidden ${
              locked ? 'cursor-default' : ''
            }`}
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-sm text-foreground">{t(labelKey)}</span>
              <span className="text-xs text-muted-foreground">{t(descriptionKey)}</span>
            </span>
            <Switch
              checked={on}
              disabled={locked}
              tabIndex={-1}
              aria-hidden
              className="pointer-events-none mt-0.5 shrink-0"
            />
          </DropdownMenuCheckboxItem>
        );
      })}
    </>
  );
}

interface ViewTabProps {
  view: TrackerViewType;
  active: boolean;
  isDefault: boolean;
  onClick: () => void;
  reorderable: boolean;
}

function ViewTab({ view, active, isDefault, onClick, reorderable }: ViewTabProps) {
  const { t } = useTranslation('trackers');

  const { labelKey, Icon } = VIEW_META[view];
  const label = t(labelKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: view,
    disabled: !reorderable,
  });

  // The selected tab lifts off the tinted strip on a card + shadow; the rest
  // recede into it, so the strip itself carries the grouping.
  const activeClass = 'bg-card font-medium text-foreground shadow-xs';
  const idleClass = 'font-normal text-muted-foreground hover:bg-card/60 hover:text-foreground';

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      title={isDefault ? t('trackersBoard.boardViewTabs.opensByDefault', { label }) : label}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? activeClass : idleClass
      } ${isDragging ? 'opacity-60' : ''}`}
      {...attributes}
      {...listeners}
      // After the dnd-kit spread: it also emits `aria-pressed`, so set the
      // selected state last to win.
      aria-pressed={active}
    >
      <Icon className="size-4" aria-hidden />
      {label}
    </button>
  );
}
